import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./UI";
import { Loader2, Zap, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const PROVIDER_BADGE = {
  anthropic: "bg-orange-400/15 text-orange-200 border-orange-400/25",
  openai: "bg-emerald-400/15 text-emerald-200 border-emerald-400/25",
  gemini: "bg-sky-400/15 text-sky-200 border-sky-400/25",
  perplexity: "bg-fuchsia-400/15 text-fuchsia-200 border-fuchsia-400/25",
  auto: "bg-cyan-400/15 text-cyan-200 border-cyan-400/25",
};
const PROVIDER_NAME = { anthropic: "Claude", openai: "ChatGPT", gemini: "Gemini", perplexity: "Perplexity", auto: "Auto" };

export const AiModelsCard = () => {
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [testing, setTesting] = useState(null);

  const load = () => {
    api.get("/ai-models").then(r => setData(r.data)).catch(() => setData(false));
    api.get("/ai-models/usage").then(r => setUsage(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (data === false) return null;
  if (!data) return <Card><div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading AI models…</div></Card>;

  const setModel = async (cap, value) => {
    const [provider, model] = value.split("::");
    try {
      await api.put("/ai-models", { capability: cap, provider, model });
      toast.success(`${PROVIDER_NAME[provider]} now powers this capability`);
      load();
    } catch { toast.error("Could not update model"); }
  };

  const reset = async (cap) => {
    try { await api.post("/ai-models/reset", { capability: cap }); toast.success("Reset to default"); load(); }
    catch { toast.error("Could not reset"); }
  };

  const test = async (c) => {
    setTesting(c.capability);
    try {
      const { data: r } = await api.post("/ai-models/test", { provider: c.provider, model: c.model });
      if (r.ok) toast.success(`${c.model} responded in ${r.latency_ms}ms`);
      else toast.error(`Model test failed: ${r.error}`);
    } catch { toast.error("Test request failed"); }
    finally { setTesting(null); }
  };

  return (
    <Card padded={false} data-testid="ai-models-card">
      <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">AI Models</div>
          <div className="text-sm text-slate-300 mt-0.5">Auto mode picks the best model per task — live web questions go to Perplexity, analysis to ChatGPT, tools to Claude. Pin any capability to a specific model below.</div>
        </div>
      </div>
      {usage && usage.by_provider?.length > 0 && (
        <div className="px-5 py-3 border-b border-white/10 flex flex-wrap items-center gap-2" data-testid="ai-usage-meter">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">Spend this month (est):</span>
          {usage.by_provider.map((p) => (
            <span key={p.provider} className={`text-[10px] px-2 py-1 rounded-full border ${PROVIDER_BADGE[p.provider] || "bg-white/5 text-slate-300 border-white/15"}`}
              data-testid={`ai-usage-${p.provider}`}>
              {PROVIDER_NAME[p.provider] || p.provider}: ${p.est_cost.toFixed(2)} · {p.calls} calls
            </span>
          ))}
          <span className="text-[10px] text-slate-400 ml-auto font-mono" data-testid="ai-usage-total">Total ≈ ${usage.total_est_cost.toFixed(2)}</span>
        </div>
      )}
      <table className="w-full text-sm">
        <tbody>
          {data.capabilities.map((c) => (
            <tr key={c.capability} className="border-b border-white/5" data-testid={`ai-model-row-${c.capability}`}>
              <td className="px-5 py-2.5 text-slate-200">{c.label}</td>
              <td className="px-2 py-2.5">
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${PROVIDER_BADGE[c.provider] || ""}`}>
                  {PROVIDER_NAME[c.provider] || c.provider}
                </span>
              </td>
              <td className="px-2 py-2.5">
                <select value={`${c.provider}::${c.model}`} onChange={(e) => setModel(c.capability, e.target.value)}
                  data-testid={`ai-model-select-${c.capability}`}
                  className="bg-slate-900/80 border border-white/15 rounded-md px-2 py-1.5 text-xs text-white focus:border-cyan-400/60 focus:outline-none w-full max-w-[240px]">
                  {data.available_models.map((m) => (
                    <option key={`${m.provider}::${m.model}`} value={`${m.provider}::${m.model}`}>{m.label}</option>
                  ))}
                </select>
              </td>
              <td className="px-2 py-2.5 whitespace-nowrap text-right pr-5">
                <button onClick={() => test(c)} disabled={testing === c.capability} title="Test this model live"
                  data-testid={`ai-model-test-${c.capability}`}
                  className="text-slate-500 hover:text-cyan-300 p-1 disabled:opacity-40">
                  {testing === c.capability ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                </button>
                {c.is_override && (
                  <button onClick={() => reset(c.capability)} title="Reset to default"
                    data-testid={`ai-model-reset-${c.capability}`}
                    className="text-slate-500 hover:text-amber-300 p-1">
                    <RotateCcw className="w-3.5 h-3.5" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
};
